import { useMemo } from 'react'
import {
  PanelRoot,
  ResizableMainArea,
  SideBarArea,
  SingleSheetRoot,
} from '../../../../containers/SingleSheetV2'
import {
  TabMainArea,
  TabPanelArea,
} from '../../../../containers/SingleSheetV2/Tab'
import { PageArea } from '../../..'
import { WbsItemType } from '../../../../../domain/entity/WbsItemEntity'
import { WbsItemProps } from '../..'
import { MultilineTextEditorTab } from '../tab-panel/multiline-text'
import { ProjectPlanTab } from '../tab-panel/project-plan'
import { AttachmentTab } from '../tab-panel/attachment'
import { DiscussionTab } from '../tab-panel/discussion'
import { WbsItemHeader } from '../header'
import { WbsItemSubHeader } from '../subheader'
import { DevelopmentTabPanel } from '../tab-panel/DevelopmentTabPanel'
import { ChangeLogTabPanel } from '../tab-panel/change-log'
import { useWbsItemSelectOptions } from '../../hooks/wbsItemSelectOptions'
import { useWbsItemSingleSheetData } from '../../hooks/wbsItemData'
import Loading from '../../../../components/process-state-notifications/Loading'
import { useAttachmentHandlers } from '../../hooks/attachments'
import { useWbsItemPropertySearch } from '../../hooks/wbsItemPropertySearch'
import { useWbsItemChangeLogParser } from '../../hooks/wbsItemChangeLogParser'
import { useWbsItemModelModifier } from '../../hooks/wbsItemModelModifier'
import { useWbsItemLabel } from '../../hooks/label'
import { SingleSheetWrapper } from '.'
import { useWbsItemAdditionalPropertiesBySingleType } from '../../../../hooks/useWbsItemAdditionalProperties'
import { useSingleSheetKeyBind } from '../../../../containers/SingleSheetV2/hooks/keyBind'
import CancelConfirmDialog, {
  useCancelConfirmDialog,
} from '../../../../components/dialogs/CancelConfirmDialog'
import { useResizableSingleSheet } from '../../../../containers/SingleSheetV2/hooks/resizableArea'
import {
  useWbsItemLeftTabs,
  WbsItemLeftTab,
  WbsItemLeftTabs,
} from '../tab/LeftTabs'
import {
  useWbsItemRightTabs,
  WbsItemRightTab,
  WbsItemRightTabs,
} from '../tab/RightTabs'
import { useBrowserTabNameSetter } from '../../hooks/browserTabNameSetter'
import { useSingleSheetFormValidation } from '../../../../containers/SingleSheetV2/hooks/formValidation'
import { useSubmitButton } from '../../../../containers/SingleSheetV2/hooks/submitButton'
import { useDeliverableListBasicInformationTableDef } from './basic-information-table-def/deliverableList'
import { BasicInformationTable } from '../../../../containers/SingleSheetV2/table'
import { useFunctionPropertyConfiguration } from '../../hooks/functionPropertyConfiguration'
import { useMultiLineTexTabDefs } from './tab-def'

export const DeliverableList = ({
  projectUuid,
  wbsItemType,
  uuid,
  functionUuid,
  functionLayerDepth,
}: WbsItemProps) => {
  const singleSheetRef = useResizableSingleSheet<HTMLDivElement>()
  const {
    initialized,
    edited,
    editedFormData,
    isLoading,
    formModel,
    onChange,
    onChangeAdditionalPropertyValue: _onChangeAdditionalPropertyValue,
    reload,
    submit,
  } = useWbsItemSingleSheetData(
    uuid,
    projectUuid,
    WbsItemType.DELIVERABLE_LIST,
    wbsItemType
  )

  useBrowserTabNameSetter(
    formModel.displayName || '',
    functionLayerDepth,
    initialized
  )

  /**
   * Show the description tab only during creating wbs item on SingleSheet.
   * Now this case is only SingleSheet is opened on ProjectPlan when users create a ticket.
   */
  const isCreatingWbsItem = false
  const { leftTab, onChangeLeftTab } = useWbsItemLeftTabs(isCreatingWbsItem)
  const { rightTab, onChangeRightTab } = useWbsItemRightTabs(isCreatingWbsItem)
  const {
    subStatuses,
    difficulties,
    priorities,
    getSubStatusByStatus,
    getStatusBySubStatus,
  } = useWbsItemSelectOptions(projectUuid, WbsItemType.DELIVERABLE_LIST)
  const { searchTeam, searchMember } = useWbsItemPropertySearch(
    projectUuid,
    formModel
  )

  const { wbsItemAdditionalProperties } =
    useWbsItemAdditionalPropertiesBySingleType(projectUuid, wbsItemType)
  const propertyConfiguration = useFunctionPropertyConfiguration(
    projectUuid,
    wbsItemType,
    undefined
  )

  const basicInformationTableDef = useDeliverableListBasicInformationTableDef(
    projectUuid,
    {
      subStatuses,
      difficulties,
      priorities,
    },
    {
      searchTeam,
      searchMember,
    },
    wbsItemAdditionalProperties,
    propertyConfiguration
  )

  const multilineTextTabDefs = useMultiLineTexTabDefs()
  const {
    validate,
    validateAllProperties,
    validationErrors,
    hasValidationErrors,
  } = useSingleSheetFormValidation(
    basicInformationTableDef,
    multilineTextTabDefs,
    edited
  )

  const getChangeLogParser = useWbsItemChangeLogParser(
    {
      subStatuses,
      difficulties,
      priorities,
    },
    wbsItemAdditionalProperties
  )

  const {
    onChangeDisplayName,
    onChangeStatus,
    onChangeSubStatus,
    onChangeTags,
    onChangeTeam,
    onChangeAccountable,
    onChangeResponsible,
    onChangeAssignee,
    onChangeWatchers,
    onChangeDifficulty,
    onChangePriority,
    onChangeEstimatedHour,
    onChangeSprint,
    onChangeScheduledDate,
    onChangeActualDate,
    onChangeDescription,
    onChangeStartIf,
    onChangeCompleteIf,
    formModelModifiers,
  } = useWbsItemModelModifier(
    onChange,
    _onChangeAdditionalPropertyValue,
    {
      getStatusBySubStatus,
      getSubStatusByStatus,
    },
    wbsItemAdditionalProperties,
    validate
  )

  const { deliverableAttachments, addAttachments, deleteAttachments } =
    useAttachmentHandlers(
      uuid,
      projectUuid,
      wbsItemType,
      formModel.deliverableAttachments
    )
  const labelMap = useWbsItemLabel(
    wbsItemAdditionalProperties,
    propertyConfiguration
  )
  const {
    statusLabel,
    subStatusLabel,
    priorityLabel,
    teamLabel,
    accountableLabel,
    responsibleLabel,
    assigneeLabel,
    scheduledDateLabel,
    actualDateLabel,
    estimatedHourLabel,
    actualHourLabel,
  } = useMemo(() => {
    return {
      statusLabel: labelMap.get('status') || '',
      subStatusLabel: labelMap.get('substatus') || '',
      priorityLabel: labelMap.get('priority') || '',
      teamLabel: labelMap.get('team') || '',
      accountableLabel: labelMap.get('accountable') || '',
      responsibleLabel: labelMap.get('responsible') || '',
      assigneeLabel: labelMap.get('assignee') || '',
      scheduledDateLabel: labelMap.get('scheduledDate') || '',
      actualDateLabel: labelMap.get('actualDate') || '',
      estimatedHourLabel: labelMap.get('estimatedHour') || '',
      actualHourLabel: labelMap.get('actualHour') || '',
    }
  }, [labelMap, labelMap.size])

  const { openDialog: openCancelConfirmDialog, ...cancelConfirmDialogProps } =
    useCancelConfirmDialog(reload)

  const { onClickSubmitButton, submitButtonIsActive } = useSubmitButton(
    submit,
    edited,
    formModel,
    hasValidationErrors,
    validateAllProperties
  )
  useSingleSheetKeyBind(onClickSubmitButton, functionUuid)

  return (
    <PageArea>
      <Loading isLoading={isLoading} />
      {initialized && (
        <>
          <WbsItemHeader
            uuid={uuid}
            isCreatingWbsItem={false}
            title={formModel.displayName || ''}
            onChangeTitle={onChangeDisplayName}
            type={wbsItemType}
            watchers={formModel.watchers}
            onChangeWatchers={onChangeWatchers}
            functionLayerDepth={functionLayerDepth}
          />
          <WbsItemSubHeader
            projectUuid={projectUuid}
            submit={onClickSubmitButton}
            reload={edited ? openCancelConfirmDialog : reload}
            submitIsActive={submitButtonIsActive}
            model={formModel}
            statusLabel={statusLabel}
            subStatusLabel={subStatusLabel}
            priorityLabel={priorityLabel}
            teamLabel={teamLabel}
            accountableLabel={accountableLabel}
            responsibleLabel={responsibleLabel}
            assigneeLabel={assigneeLabel}
            scheduledDateLabel={scheduledDateLabel}
            actualDateLabel={actualDateLabel}
            estimatedHourLabel={estimatedHourLabel}
            actualHourLabel={actualHourLabel}
            onChangeStatus={onChangeStatus}
            onChangeSubStatus={onChangeSubStatus}
            onChangePriority={onChangePriority}
            onChangeTeam={onChangeTeam}
            onChangeAccountable={onChangeAccountable}
            onChangeResponsible={onChangeResponsible}
            onChangeAssignee={onChangeAssignee}
            onChangeScheduledDate={onChangeScheduledDate}
            onChangeActualDate={onChangeActualDate}
            onChangeEstimatedHour={onChangeEstimatedHour}
            subStatuses={subStatuses}
            priorities={priorities}
          />
          <SingleSheetWrapper>
            <SingleSheetRoot ref={singleSheetRef}>
              <ResizableMainArea singleSheetRootRef={singleSheetRef}>
                <TabPanelArea>
                  <WbsItemLeftTabs
                    isCreatingWbsItem={isCreatingWbsItem}
                    leftTab={leftTab}
                    onChangeLeftTab={onChangeLeftTab}
                  />
                  <TabMainArea>
                    <PanelRoot>
                      {leftTab === WbsItemLeftTab.BASIC_INFORMATION && (
                        <BasicInformationTable
                          dataUuid={uuid}
                          tableDef={basicInformationTableDef}
                          formModel={formModel}
                          formModelModifiers={formModelModifiers}
                          editedFormData={editedFormData}
                          validationErrors={validationErrors}
                        />
                      )}
                      {leftTab === WbsItemLeftTab.DESCRIPTION && (
                        <MultilineTextEditorTab
                          uuid={uuid}
                          externalId="deliverableList.description"
                          value={formModel.description}
                          onChange={onChangeDescription}
                        />
                      )}
                      {leftTab === WbsItemLeftTab.START_IF && (
                        <MultilineTextEditorTab
                          uuid={uuid}
                          externalId="deliverableList.startIf"
                          value={formModel.startIf}
                          onChange={onChangeStartIf}
                        />
                      )}
                      {leftTab === WbsItemLeftTab.COMPLETE_IF && (
                        <MultilineTextEditorTab
                          uuid={uuid}
                          externalId="deliverableList.completeIf"
                          value={formModel.completeIf}
                          onChange={onChangeCompleteIf}
                        />
                      )}
                      {leftTab === WbsItemLeftTab.SUB_TASK && (
                        <ProjectPlanTab uuid={uuid} />
                      )}
                      {leftTab === WbsItemLeftTab.ATTACHMENT && (
                        <AttachmentTab
                          attachments={deliverableAttachments}
                          acceptingFileTypes={[]}
                          onUpload={addAttachments}
                          onDelete={deleteAttachments}
                        />
                      )}
                    </PanelRoot>
                  </TabMainArea>
                </TabPanelArea>
              </ResizableMainArea>
              <SideBarArea>
                <TabPanelArea>
                  <WbsItemRightTabs
                    isCreatingWbsItem={isCreatingWbsItem}
                    rightTab={rightTab}
                    onChangeRightTab={onChangeRightTab}
                  />
                  <TabMainArea>
                    <PanelRoot>
                      {rightTab === WbsItemRightTab.DISCUSSION && (
                        <DiscussionTab
                          functionUuid={functionUuid}
                          projectUuid={projectUuid}
                          uuid={uuid}
                          wbsItemType={wbsItemType}
                        />
                      )}
                      {rightTab === WbsItemRightTab.CHANGE_LOG && (
                        <ChangeLogTabPanel
                          uuids={[uuid]}
                          getParser={getChangeLogParser}
                          labelMap={labelMap}
                        />
                      )}
                      {rightTab === WbsItemRightTab.DEVELOPMENT && (
                        <DevelopmentTabPanel uuid={uuid} />
                      )}
                    </PanelRoot>
                  </TabMainArea>
                </TabPanelArea>
              </SideBarArea>
            </SingleSheetRoot>
          </SingleSheetWrapper>
          <CancelConfirmDialog {...cancelConfirmDialogProps} />
        </>
      )}
    </PageArea>
  )
}
